import { DropzoneArea } from "@skrice.studio/material-ui-dropzone-mui";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import * as draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { createForm } from "rc-form";
import * as React from "react";
import { Editor } from "react-draft-wysiwyg";

import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.files;
};

interface CreateItemSate {
    modalVisible: boolean;
    creatingFile: boolean;
    error: null | string;
    files: any[];
    editMode: boolean;
    editorState: any;
}

interface CreateItemProps {
    buttonText?: string | Element;
    buttonIcon?: boolean;
    itemType: string;
    refreshParent(): void;
    modalTitle: string;
    item?: any;
    lastItemId?: null | number;
    form: any;
    classes: any;
}

class ManageNewsWrapped extends React.Component<CreateItemProps, CreateItemSate> {

    constructor(props) {
        super(props);
        // List with grid items

        this.state = {
            // Flags
            modalVisible: false,
            error: null,
            creatingFile: false,
            files: [],
            editorState: this.defaultEditorState(),
            editMode: !!this.props.item,
        };
    }

    defaultEditorState = () => {
        if (this.props.item) {
            const contentBlock = htmlToDraft(this.props.item.body);

            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                const editorState = EditorState.createWithContent(contentState);

                return editorState;
            }
        }

        return EditorState.createEmpty();
    }

    /**
     * Callback function for create button
     * Calling this function a modal will be shown
     */
    showModal() {
        this.setState({
            modalVisible: true,
        });
    }

    handleModalCancel() {
        this.setState({
            modalVisible: false,
            error: null,
            files: [],
            editorState: EditorState.createEmpty(),
        }, () => {
            // Reset form values
            this.props.form.resetFields();
        });
    }

    handleModalCreate() {
        const self = this;

        this.props.form.validateFields((err, values) => {
            if (err) {
                return;
            }
            const formData = new FormData();
            let endpoint;

            this.state.files.forEach((file) => {
                formData.append("file", file);
            });

            formData.append("title", values.title);
            formData.append("body", draftToHtml(convertToRaw(this.state.editorState.getCurrentContent())));
            formData.append("published", values.published);
            formData.append("popup", values.popup)
            formData.append("background", values.background);
            formData.append("type", values.type);

            if (values.subtitle) {
                formData.append("subtitle", values.subtitle);
            }

            if (values.subtitleColor) {
                formData.append("subtitle_color", values.subtitleColor);
            }

            if (this.state.editMode && this.props.item) {
                formData.append("id", this.props.item.id);

                endpoint = axios.patch(`${Config.getInstance.apiUrl}/api/news`, formData, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });
            } else {
                endpoint = axios.post(`${Config.getInstance.apiUrl}/api/news`, formData, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                });
            }

            this.setState({ creatingFile: true }, () => {
                endpoint.then((result) => {
                    // Reset form fields
                    self.props.form.resetFields();

                    // Otherwise load and show a list with applications
                    return this.setState({
                        creatingFile: false,
                        modalVisible: false,
                        files: [],
                    }, () => {
                        // Refresh parent component
                        this.props.refreshParent();
                    });
                }).catch((error) => {
                    this.setState({
                        error: error.response.statusText,
                        creatingFile: false,
                        files: [],
                    });

                    PopupMessage.error("Something went wrong");
                });
            });
        });
    }

    chooseFile(files) {
        this.setState({ files });
    }

    onEditorStateChange = (editorState) => {
        this.setState({
            editorState,
        });
    }

    onTypeChange = () => {
        this.props.form.resetFields();
    }

    render() {
        const { getFieldDecorator, getFieldValue } = this.props.form;
        const { classes } = this.props;

        return (
            <React.Fragment>
                {this.props.buttonIcon
                    ? <span onClick={() => this.showModal()} style={{ cursor: "pointer", display: "inline-block" }}>
                        {this.props.buttonText}
                    </span>
                    : <Button disableElevation color="primary" onClick={() => this.showModal()} size="small" variant="contained">
                        {this.props.buttonText}
                    </Button>
                }

                <Dialog
                    onClose={() => this.handleModalCancel()}
                    open={this.state.modalVisible}
                    fullWidth={true}
                    maxWidth="md"
                >
                    <DialogTitle id="simple-dialog-title">
                        {`${this.state.editMode ? "Edit" : "Add"} ${!getFieldValue("type") || getFieldValue("type") === "news" ? "article" : "patch notes"}`}
                    </DialogTitle>

                    <DialogContent>
                        <div>
                            <Grid container spacing={2}>
                                <Grid item xs={6} spacing={2}>
                                    <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                        <InputLabel id="published-label-id">Type</InputLabel>

                                        {getFieldDecorator("type", {
                                            rules: [{
                                                required: true,
                                                message: "Please select type",
                                            }],
                                            onChange: this.onTypeChange,
                                            initialValue: this.props.item ? this.props.item.type : "news",
                                        })(
                                            <Select
                                                labelId="published-label-id"
                                                id="published-label-id"
                                                label="Published"
                                            >
                                                <MenuItem value="news">News</MenuItem>
                                                <MenuItem value="path_notes">Patch notes</MenuItem>
                                            </Select>,
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6} spacing={2}>
                                    <FormControl style={{ marginTop: 15, width: "100%" }}>
                                        {getFieldDecorator("title", {
                                            initialValue: this.props.item ? this.props.item.title : "",
                                        })(
                                            <TextField
                                                label={getFieldValue("type") === "news" ? "Title" : "Version"}
                                                style={{ width: "100%" }}
                                                variant="outlined"
                                            />,
                                        )}
                                    </FormControl>
                                </Grid>

                                {getFieldValue("type") === "path_notes" && <React.Fragment>
                                    <Grid item xs={6} spacing={2}>
                                        <FormControl style={{ marginTop: 15, width: "100%" }}>
                                            {getFieldDecorator("subtitle", {
                                                initialValue: this.props.item ? this.props.item.subtitle : "",
                                            })(
                                                <TextField
                                                    label="Subtitle"
                                                    style={{ width: "100%" }}
                                                    variant="outlined"
                                                />,
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} spacing={2}>
                                        <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                            <InputLabel id="published-label-id">Subtitle color</InputLabel>

                                            {getFieldDecorator("subtitleColor", {
                                                initialValue: this.props.item ? this.props.item.subtitle_color : "#2cfaff",
                                            })(
                                                <Select
                                                    labelId="published-label-id"
                                                    id="published-label-id"
                                                    label="Published"
                                                >
                                                    <MenuItem value="#ffe161">Yellow</MenuItem>
                                                    <MenuItem value="#2cfaff">Blue</MenuItem>
                                                </Select>,
                                            )}
                                        </FormControl>
                                    </Grid>
                                </React.Fragment>}

                                <Grid item xs={6} spacing={2}>
                                    <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                        <InputLabel id="published-label-id">Published</InputLabel>

                                        {getFieldDecorator("published", {
                                            rules: [{
                                                required: true,
                                                message: "Please input the item id",
                                            }],
                                            initialValue: this.props.item ? this.props.item.published : "false",
                                        })(
                                            <Select
                                                labelId="published-label-id"
                                                id="published-label-id"
                                                label="Published"
                                            >
                                                <MenuItem value="true">True</MenuItem>
                                                <MenuItem value="false">False</MenuItem>
                                            </Select>,
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6} spacing={2}>
                                    <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                        <InputLabel id="background-label-id">Background</InputLabel>

                                        {getFieldDecorator("background", {
                                            rules: [{
                                                required: true,
                                                message: "Please select background color",
                                            }],
                                            initialValue: this.props.item ? this.props.item.background : "blue",
                                        })(
                                            <Select
                                                labelId="background-label-id"
                                                id="background-label-id"
                                                label="Background"
                                            >
                                                <MenuItem value="blue">Blue</MenuItem>
                                                <MenuItem value="red">Red</MenuItem>
                                            </Select>,
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6} spacing={2}>
                                    <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                        <InputLabel id="popup-label-id">Pop-up</InputLabel>

                                        {getFieldDecorator("popup", {
                                            rules: [{
                                                required: true,
                                                message: "Please select option",
                                            }],
                                            initialValue: this.props.item ? this.props.item.popup : "false",
                                        })(
                                            <Select
                                                labelId="popup-label-id"
                                                id="popup-label-id"
                                                label="popup"
                                            >
                                                <MenuItem value="true">True</MenuItem>
                                                <MenuItem value="false">False</MenuItem>
                                            </Select>,
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid xs={12}>
                                <FormControl style={{ marginTop: 15, width: "100%" }} variant="outlined">
                                    <InputLabel id="published-label-id">Content</InputLabel>

                                    <div style={{ float: "left", marginTop: "3em", background: "#fff", color: "#000" }}>
                                        <Editor
                                            editorState={this.state.editorState}
                                            toolbarClassName="toolbarClassName"
                                            wrapperClassName="wrapperClassName"
                                            editorClassName="editorClassName"
                                            onEditorStateChange={this.onEditorStateChange}
                                            toolbar={{
                                                options: ["inline", "list", "textAlign", "link", "history"],
                                            }}
                                        />
                                    </div>
                                </FormControl>
                            </Grid>

                            {getFieldValue("type") === "news" && <Grid xs={12}>
                                <FormControl style={{ marginTop: 15, width: "100%" }}>
                                    <InputLabel id="image-label" style={{ float: "left" }}>Image</InputLabel>

                                    <div className="dropbox" style={{ float: "left", overflow: "hidden", marginTop: 55 }}>
                                        {getFieldDecorator("dragger", {
                                            valuePropName: "files",
                                            getValueFromEvent: normFile,
                                        })(
                                            <DropzoneArea
                                                onChange={(e) => this.chooseFile(e)}
                                                acceptedFiles={["image/jpeg", "image/png", "image/jpg", "video/mp4"]}
                                                showFileNamesInPreview={true}
                                                filesLimit={1}
                                                showFileNames={true}
                                            />
                                        )}
                                    </div>

                                    {this.props?.item?.image && <div style={{ marginTop: 20 }}>
                                        <img src={`${Config.getInstance.config.s3.news}/${this.props?.item?.image}`} style={{ maxWidth: 400 }}/>
                                    </div>}
                                </FormControl>
                            </Grid>}

                        </div>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            disableElevation
                            onClick={() => this.handleModalCancel()}
                            color="neutral"
                            variant="contained"
                            disabled={this.state.creatingFile}
                            style={{ marginRight: 10 }}
                        >
                            Cancel
                        </Button>

                        <div className={classes.wrapper}>
                            <Button
                                disableElevation
                                onClick={() => this.handleModalCreate()}
                                color="primary"
                                variant="contained"
                                disabled={this.state.creatingFile}
                            >
                                {this.state.editMode ? "Edit" : "Add"}
                            </Button>

                            {this.state.creatingFile && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

const styles: any = (_) => ({
    wrapper: {
        position: "relative",
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
});

export const ManageNews = createForm()(withStyles(styles)(ManageNewsWrapped));
